import { FC, MouseEvent, PropsWithChildren } from "react";
import { toast } from "react-hot-toast";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import _noop from "lodash/noop";

import { clsxMerge } from "shared/lib/helpers/styles";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

interface CopyToClipboardPropsI extends PropsWithClassNameI, PropsWithChildren {
  textToCopy?: string;
  showCopyIcon?: boolean;
  toastMessage?: string;
  isDisabled?: boolean;
  /**
   * Confines copying to the icon button and leaves the children inert.
   *
   * Use it whenever the children are interactive on their own, such as a
   * click-to-call button or a link. Without it their click bubbles into the
   * wrapper and fires both their own action and a copy.
   */
  isIconClickOnly?: boolean;
  iconAriaLabel?: string;
}

const TEXT_TO_IGNORE = ["-"];

const DEFAULT_ICON_ARIA_LABEL = "Copy to clipboard";

const CopyToClipboard: FC<CopyToClipboardPropsI> = ({
  textToCopy,
  children,
  className,
  showCopyIcon = false,
  toastMessage,
  isDisabled = false,
  isIconClickOnly = false,
  iconAriaLabel = DEFAULT_ICON_ARIA_LABEL,
}) => {
  const text = textToCopy ?? "";
  const hasTextToCopy = text.length > 0 && !TEXT_TO_IGNORE.includes(text);
  const isCopyEnabled = !isDisabled && hasTextToCopy;

  const copy = () => {
    if (!isCopyEnabled) {
      return;
    }

    if (!navigator.clipboard) {
      toast.error("Copying isn't available in this browser");
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(toastMessage || `Copied to clipboard "${text}"`);
      })
      .catch(() => {
        // The value is deliberately left out: callers pass arbitrary field
        // content here, and an error does not need to echo it back.
        toast.error("Failed to copy to clipboard");
      });
  };

  const handleIconClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    copy();
  };

  /**
   * In icon-click-only mode the icon is the sole affordance, so it has to be
   * there whenever copying is possible, and gone when it is not, so nobody is
   * handed a control that silently does nothing.
   */
  const isIconVisible = isIconClickOnly
    ? isCopyEnabled
    : showCopyIcon && !isDisabled;

  return (
    <div
      className={clsxMerge(
        "flex w-fit",
        !isIconClickOnly && "cursor-pointer",
        (showCopyIcon || isIconClickOnly) && "items-center gap-2",
        className
      )}
      onClick={isIconClickOnly || isDisabled ? _noop : copy}
    >
      {children}

      {isIconVisible &&
        (isIconClickOnly ? (
          <button
            type="button"
            aria-label={iconAriaLabel}
            onClick={handleIconClick}
            className="shrink-0 cursor-pointer"
          >
            <DocumentDuplicateIcon className="h-4 w-4 text-black" />
          </button>
        ) : (
          <DocumentDuplicateIcon className="h-4 w-4 text-black" />
        ))}
    </div>
  );
};

export default CopyToClipboard;
